import React, { useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { PageHeader, Card, Section } from "@/components/Primitives";
import FilterBar from "@/components/FilterBar";
import { FileXls, FileCsv, FilePdf, Printer, DownloadSimple, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const qs = (f) => {
  const p = new URLSearchParams();
  Object.entries(f || {}).forEach(([k, v]) => { if (v) p.append(k, v); });
  return p.toString();
};

const PNG_CHARTS = [
  { kind: "monthly", label: "Monthly trend" },
  { kind: "yoy", label: "YoY comparison" },
  { kind: "brands", label: "Top brands" },
  { kind: "locations", label: "Top locations" },
  { kind: "salesmen", label: "Top salesmen" },
  { kind: "target", label: "Target vs YTD" },
];

const Export = () => {
  const [filters, setFilters] = useState({ preset: "ytd" });
  const [busy, setBusy] = useState(null);

  const download = async (path, name, mime) => {
    setBusy(path);
    try {
      const res = await api.get(path, { responseType: "blob" });
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally { setBusy(null); }
  };

  const q = qs(filters);

  const tiles = [
    { key: "excel", label: "Excel Workbook", hint: "Multi-sheet: KPIs, Brands, Locations, Salesmen, Daily trend", icon: FileXls, action: () => download(`/export/excel?${q}`, "sales_analytics.xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") },
    { key: "csv", label: "CSV (Brands)", hint: "Brand breakdown in plain CSV", icon: FileCsv, action: () => download(`/export/csv?dataset=brands&${q}`, "brands.csv", "text/csv") },
    { key: "pdf", label: "PDF Report", hint: "Branded executive summary PDF", icon: FilePdf, action: () => download(`/export/pdf?${q}`, "sales_analytics.pdf", "application/pdf") },
    { key: "print", label: "Direct Print", hint: "Print the dashboard with optimized layout", icon: Printer, action: () => window.print() },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Export Center"
        title="Take it with you"
        subtitle="Generate print-ready Excel, PDF, CSV, PNG — or send the dashboard straight to your printer. All exports respect the filters you set below."
      />
      <FilterBar value={filters} onChange={setFilters} />

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {tiles.map((t) => (
          <Card key={t.key} testId={`export-${t.key}`} className="flex flex-col">
            <t.icon weight="duotone" size={40} className="text-myb-500 mb-3" />
            <div className="font-display text-2xl leading-tight font-light">{t.label}</div>
            <div className="text-xs text-muted-foreground mt-1 flex-1">{t.hint}</div>
            <button
              data-testid={`export-${t.key}-btn`}
              disabled={busy === t.key}
              onClick={t.action}
              className="mt-4 inline-flex items-center justify-center gap-2 text-sm bg-myb-500 text-white hover:bg-myb-600 rounded-sm py-2.5 disabled:opacity-40 transition-all"
            >
              <DownloadSimple size={14} /> {busy === t.key ? "Working…" : "Generate"}
            </button>
          </Card>
        ))}
      </div>

      <Section eyebrow="Chart PNGs" title="Server-rendered chart images">
        <Card testId="export-png-grid">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PNG_CHARTS.map((c) => (
              <button
                key={c.kind}
                data-testid={`export-png-${c.kind}`}
                onClick={() => download(`/export/chart-png?kind=${c.kind}&${q}`, `chart_${c.kind}.png`, "image/png")}
                disabled={busy === `/export/chart-png?kind=${c.kind}&${q}`}
                className="group flex items-center gap-3 p-4 border hairline rounded-sm hover:border-myb-500 hover:bg-myb-50/40 transition-all text-left"
              >
                <div className="w-10 h-10 rounded-sm bg-myb-50 flex items-center justify-center text-myb-500 group-hover:bg-myb-500 group-hover:text-white transition-all">
                  <ImageIcon weight="duotone" size={20} />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">{c.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">PNG · Headless render</div>
                </div>
                <DownloadSimple size={14} className="text-muted-foreground group-hover:text-myb-500" />
              </button>
            ))}
          </div>
          <div className="mt-4 text-xs text-muted-foreground">
            Chart images are rendered on the server by a headless matplotlib pipeline using the Behbehani palette. All filters above apply.
          </div>
        </Card>
      </Section>
    </div>
  );
};

export default Export;
